import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
// @ts-ignore - API is a JavaScript file without type definitions
import API from '../context/API.js';
import moment from 'moment';

// Types
export interface SalesSummary {
  grossSales: number;
  netSales: number;
  orderCount: number;
  totalRevenue: number;
  itemsSold: number;
  tips: number;
  averageTipRate: number;
}

export interface Period {
  startDate: string;
  endDate: string;
  totalDays: number;
}

export interface TimeSeriesItem {
  period: string;
  amount: number;
  count: number;
  averageOrderValue: number;
}

export interface CustomerStats {
  totalCustomers: number;
  newCustomers: number;
  repeatCustomers: number;
  averageSpend: number;
}

export interface Category {
  name: string;
  revenue: number;
  orders: number;
  averageOrderValue: number;
}

export interface CategorySales {
  categories: Category[];
}

export interface Item {
  name: string;
  revenue: number;
  quantity: number;
  averagePrice: number;
}

export interface ItemSales {
  items: Item[];
}

export interface CustomRangeReportData {
  merchantName?: string;
  reportType?: string;
  period?: Period;
  salesSummary: SalesSummary;
  timeSeriesData?: TimeSeriesItem[];
  customerStats?: CustomerStats;
  categorySales?: CategorySales;
  itemSales?: ItemSales;
}

export interface CustomRangeReportsState {
  reportData: CustomRangeReportData | null;
  loading: boolean;
  error: string | null;
  stores: Array<{ id: string; name: string; [key: string]: any }>;
  storesLoading: boolean;
}

const initialState: CustomRangeReportsState = {
  reportData: null,
  loading: false,
  error: null,
  stores: [],
  storesLoading: false,
};

// Async thunks
export const fetchCustomRangeReport = createAsyncThunk(
  'customRangeReports/fetchCustomRangeReport',
  async (
    params: {
      startDate: string;
      endDate: string;
      merchantId?: string;
      storeId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('/analytics/reports/custom-range', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          merchantId: params.merchantId,
          store: params.storeId,
        },
      });

      return response.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch custom range report'
      );
    }
  }
);

export const fetchStores = createAsyncThunk(
  'customRangeReports/fetchStores',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('/stores', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch stores'
      );
    }
  }
);

const customRangeReportsSlice = createSlice({
  name: 'customRangeReports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearReportData: (state) => {
      state.reportData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch custom range report
      .addCase(fetchCustomRangeReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomRangeReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reportData = action.payload;
      })
      .addCase(fetchCustomRangeReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch stores
      .addCase(fetchStores.pending, (state) => {
        state.storesLoading = true;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.storesLoading = false;
        state.stores = action.payload;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.storesLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearReportData } = customRangeReportsSlice.actions;
export default customRangeReportsSlice.reducer;
